import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useUI } from '@/store/ui'
import { SigilsAPI } from '@/api/sigils'
import { groupSigils, sigilSummary, type SigilGroups } from '@/lib/sigils'
import { SigilCard } from '@/components/Sigils'
import { HelpHint } from '@/components/HelpHint'

// The sigils gallery — every mark of progression across the suite, grouped by how close it
// is to being yours: earned, in reach, locked, and coming with a sibling app. Diwa lights up
// the marks you earn here today; Sanctum and Eden add their own regions of the same wall.

const BANDS: { key: keyof SigilGroups; title: string; note: string }[] = [
  { key: 'earned', title: 'Earned', note: 'Marks you already hold — carried across the whole suite.' },
  { key: 'inReach', title: 'In reach', note: 'A few days of using Diwa away — keep going.' },
  { key: 'locked', title: 'Locked', note: 'Rarer marks and honors — earned over the long arc.' },
  { key: 'comingSoon', title: 'Coming soon', note: 'Dormant until their app ships — Sanctum & Eden.' },
]

export function Sigils() {
  const { diwaUrl } = useUI()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['sigils'],
    queryFn: () => new SigilsAPI(diwaUrl).listSigils(),
  })
  const sigils = data?.sigils ?? []
  const groups = useMemo(() => groupSigils(sigils), [sigils])

  return (
    <div>
      <div className="dw-page-header">
        <div>
          <div className="dw-page-title">
            Sigils <HelpHint gloss="Suite-wide marks of progression — earned from your own activity, carried across every Diwata app." />
          </div>
          <div className="dw-page-subtitle">
            Earn sigils across the suite — Diwa today, Sanctum &amp; Eden soon.
            {sigils.length > 0 && <> {sigilSummary(sigils)}.</>}
          </div>
        </div>
      </div>

      {isLoading && <p className="dw-empty">Reading your sigils…</p>}
      {isError && <p className="dw-error">Couldn't load your sigils — try again shortly.</p>}
      {data && sigils.length === 0 && (
        <div className="dw-rail-empty">No sigils defined yet — they arrive with the awards system.</div>
      )}

      {data &&
        BANDS.map(({ key, title, note }) => {
          const band = groups[key]
          if (band.length === 0) return null
          return (
            <section key={key} style={{ marginBottom: 28 }}>
              <div className="dw-section-header" style={{ marginBottom: 10 }}>
                <div className="dw-rail-section-title" style={{ marginBottom: 0 }}>
                  {title} · {band.length}
                </div>
                <span className="dw-inbox-detail">{note}</span>
              </div>
              <div className="dw-sigils-grid">
                {band.map((s) => (
                  <SigilCard key={s.key} sigil={s} />
                ))}
              </div>
            </section>
          )
        })}
    </div>
  )
}
